import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Lock, ShieldCheck } from "lucide-react";

const PLANS = [
  {
    key: "start", name: "START", price: "CHF 179.–", tagline: "Für den professionellen Start im Web.",
    setup: "Setup CHF 690.–", features: ["Professionelle Website", "Mobil-optimiert", "Cockpit-Zugang", "Laufende Betreuung"],
  },
  {
    key: "business", name: "BUSINESS", price: "CHF 249.–", tagline: "Mehr Sichtbarkeit und Funktionen.", popular: true,
    setup: "Setup CHF 990.–", features: ["Alles aus START", "Erweiterte Seitenstruktur", "Lead-Formulare", "Performance-Auswertung"],
  },
  {
    key: "wachstum", name: "WACHSTUM", price: "CHF 390.–", tagline: "Für wachsende Unternehmen.",
    setup: "Setup CHF 1’490.–", features: ["Alles aus BUSINESS", "Mehrere Module & Booster", "Priorisierte Betreuung", "Monatsreport"],
  },
];

const Plan = ({ p }) => (
  <div
    data-testid={`plan-${p.key}`}
    className={`relative flex flex-col rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1 ${
      p.popular ? "border-[#A3E635]/60 bg-[#A3E635]/[0.06] shadow-[0_0_40px_-12px_rgba(163,230,53,0.45)]" : "border-white/10 bg-white/[0.03] hover:border-white/20"
    }`}
  >
    {p.popular && (
      <span className="absolute -top-3 left-7 bg-[#A3E635] text-black text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
        Beliebt
      </span>
    )}
    <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-gray-300">{p.name}</h3>
    <p className="text-sm text-gray-500 mt-1 min-h-[40px]">{p.tagline}</p>
    <div className="mt-5">
      <div className="flex items-end gap-2">
        <span className="text-4xl font-black tracking-tighter text-white">{p.price}</span>
        <span className="text-gray-400 text-sm mb-1">/ Monat</span>
      </div>
      <div className="text-[#A3E635] text-sm font-semibold mt-1">inkl. MWST</div>
    </div>
    <div className="text-xs text-gray-400 mt-4 pb-5 border-b border-white/10">
      {p.setup} · Mindestlaufzeit 24 Monate
    </div>
    <ul className="space-y-2.5 mt-5 mb-7 flex-1">
      {p.features.map((f, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
          <Check className="w-4 h-4 text-[#A3E635] mt-0.5 shrink-0" /> {f}
        </li>
      ))}
    </ul>
    <a
      data-testid={`plan-cta-${p.key}`}
      href={`mailto:info@kewagpage.ch?subject=Anfrage%20Paket%20${p.name}`}
      className={`text-center py-3 px-5 rounded-lg font-semibold text-sm transition-colors ${
        p.popular ? "bg-[#A3E635] text-black hover:bg-[#b6f15a]" : "bg-white/5 text-white border border-white/10 hover:bg-white/10"
      }`}
    >
      {p.name} anfragen
    </a>
  </div>
);

export default function PricingPublic() {
  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-2" data-testid="brand-home">
          <span className="text-xl font-black tracking-tight">KEWAG<span className="text-[#A3E635]">Page</span></span>
          <span className="hidden sm:inline text-[11px] text-gray-500">by KEWAG &amp; Partner GmbH</span>
        </Link>
        <Link to="/login" data-testid="nav-login" className="text-sm text-gray-300 hover:text-[#A3E635] transition-colors flex items-center gap-1">
          Login <ArrowRight className="w-4 h-4" />
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 pb-24">
        <div className="text-center max-w-2xl mx-auto pt-10 pb-14">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/20 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Website-Abos für Schweizer KMU
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter" data-testid="pricing-title">
            Unsere Website-Abos
          </h1>
          <p className="text-base md:text-lg text-gray-400 mt-5">
            START, BUSINESS und WACHSTUM sind unsere drei Standard-Abos. INDIVIDUELL ist für grössere
            Websites, mehrere Standorte oder spezielle Funktionen.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {PLANS.map((p) => <Plan key={p.key} p={p} />)}

          <div data-testid="plan-individuell" className="relative flex flex-col rounded-2xl border border-dashed border-white/20 bg-white/[0.02] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#A3E635]/40">
            <span className="absolute -top-3 left-7 bg-white/10 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-white/15">
              Individuell
            </span>
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-gray-300">INDIVIDUELL</h3>
            <p className="text-sm text-gray-500 mt-1 min-h-[40px]">Für grössere oder spezielle Projekte.</p>
            <div className="mt-5">
              <span className="text-3xl font-black tracking-tighter text-white">Persönliches Angebot</span>
            </div>
            <div className="text-xs text-gray-400 mt-4 pb-5 border-b border-white/10">
              Setup und Monatspreis nach Projektumfang.
            </div>
            <p className="text-sm text-gray-300 mt-5 mb-7 flex-1">
              Für grössere Websites, mehrere Standorte oder spezielle Funktionen. Wir erstellen Ihnen
              ein massgeschneidertes Angebot.
            </p>
            <a
              data-testid="plan-cta-individuell"
              href="mailto:info@kewagpage.ch?subject=Anfrage%20Individuelle%20L%C3%B6sung"
              className="text-center py-3 px-5 rounded-lg font-semibold text-sm bg-white/5 text-white border border-white/10 hover:bg-white/10 transition-colors"
            >
              Angebot anfragen
            </a>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-10" data-testid="pricing-footnote">
          Alle Preise in CHF, inkl. 8.1% MWST. Mindestlaufzeit 24 Monate. Fragen? <a href="mailto:info@kewagpage.ch" className="text-[#A3E635] hover:underline">info@kewagpage.ch</a>
        </p>
        <div data-testid="trust-badge" className="mt-5 flex items-center justify-center gap-4 text-[11px] text-gray-500">
          <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#A3E635]" /> Verschlüsselte Verbindung</span>
          <span className="text-gray-700">·</span>
          <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#A3E635]" /> 2FA-geschützt</span>
        </div>
      </main>
    </div>
  );
}
